import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isApiError } from '@/lib/api/errors';
import { ar } from './ar';

export type Lang = 'ar' | 'en';
export type MessageKey = keyof typeof ar;
export type Dictionary = Record<MessageKey, string>;
export type Params = Record<string, string | number>;
export type Translate = (key: MessageKey, params?: Params) => string;

const STORAGE_KEY = 'tabashir.lang';

export function storedLang(): Lang {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'ar';
  } catch {
    return 'ar';
  }
}

/** Arabic ships in the main bundle; English loads on demand. */
export async function loadDictionary(lang: Lang): Promise<Dictionary> {
  return lang === 'ar' ? ar : (await import('./en')).en;
}

export function interpolate(message: string, params?: Params): string {
  if (!params) return message;
  return message.replace(/\{(\w+)\}/g, (m, name: string) => (name in params ? String(params[name]) : m));
}

export function applyDocumentLang(lang: Lang): void {
  const html = document.documentElement;
  html.lang = lang;
  html.dir = lang === 'ar' ? 'rtl' : 'ltr';
}

interface I18nValue {
  lang: Lang;
  dir: 'rtl' | 'ltr';
  t: Translate;
  /** Translates an API error code; falls back to a generic message. */
  errorText: (error: unknown) => string;
  setLang: (lang: Lang) => Promise<void>;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ initialLang, initialDict, children }: { initialLang: Lang; initialDict: Dictionary; children: ReactNode }) {
  const [{ lang, dict }, setState] = useState({ lang: initialLang, dict: initialDict });

  useEffect(() => applyDocumentLang(lang), [lang]);

  const setLang = useCallback(async (next: Lang) => {
    const nextDict = await loadDictionary(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
    setState({ lang: next, dict: nextDict });
  }, []);

  const value = useMemo<I18nValue>(() => {
    const t: Translate = (key, params) => interpolate(dict[key] ?? key, params);
    const errorText = (error: unknown) => {
      if (isApiError(error)) {
        const key = `error.${error.code}` as MessageKey;
        return key in dict ? t(key) : t('error.ERROR');
      }
      return t('error.ERROR');
    };
    return { lang, dir: lang === 'ar' ? 'rtl' : 'ltr', t, errorText, setLang };
  }, [lang, dict, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n outside I18nProvider');
  return ctx;
}

export function useT(): Translate {
  return useI18n().t;
}
